'use client';

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useController, useFormContext, useFormState } from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { Checkbox, SelectInput, TextInput } from '@eliadmin/core/src/components/nt/forms/controls';
import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';

import type { RegisterFormInput } from './register-schema';

type TextName = Exclude<
  keyof RegisterFormInput,
  'company_buy' | 'other_delivery' | 'consent' | 'newsletter'
>;
type BooleanName = 'other_delivery' | 'consent' | 'newsletter';

/** DOM id of a registration field („delivery_city“ → „register-delivery-city“). */
export const fieldId = (name: keyof RegisterFormInput) => `register-${name.replace(/_/g, '-')}`;

/** The field's error message (subscribed to that field only). */
function useFieldError(name: keyof RegisterFormInput) {
  const { control, getFieldState } = useFormContext<RegisterFormInput>();
  const state = useFormState({ control, name });
  return getFieldState(name, state).error?.message;
}

/** Figma „Form / Input“ bound to the registration: label, the 48 px box, helper or error. */
export function RegisterTextField({
  name,
  label,
  required,
  hint,
  action,
  className,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'id'> & {
  name: TextName;
  label: string;
  hint?: ReactNode;
  /** Right slot inside the box („Vyhľadať“). */
  action?: ReactNode;
}) {
  const { register } = useFormContext<RegisterFormInput>();
  const error = useFieldError(name);
  const id = fieldId(name);

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint || undefined}
      error={error}
      className={className}
    >
      <TextInput
        id={id}
        invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={describedBy(id, { error, hint: hint || undefined })}
        action={action}
        {...input}
        {...register(name)}
      />
    </FormField>
  );
}

/** „Krajina“ – the store's countries in the searchable select with the Figma caret (always set). */
export function RegisterCountryField({
  name,
  label,
  countries,
  autoComplete,
}: {
  name: 'country' | 'delivery_country';
  label: string;
  countries: Country[];
  autoComplete: string;
}) {
  const { control } = useFormContext<RegisterFormInput>();
  const { field } = useController({ control, name });
  const error = useFieldError(name);
  const id = fieldId(name);

  return (
    <FormField id={id} label={label} required error={error}>
      <SelectInput
        id={id}
        invalid={!!error}
        aria-required
        aria-describedby={describedBy(id, { error })}
        autoComplete={autoComplete}
        {...field}
        label={label}
        options={countries.map(country => ({ value: String(country.id), label: country.name }))}
      />
    </FormField>
  );
}

/** Figma „Form / Checkbox“ bound to a boolean of the registration; an error under the box. */
export function RegisterCheckbox({
  name,
  label,
  required,
}: {
  name: BooleanName;
  label: ReactNode;
  required?: boolean;
}) {
  const { register } = useFormContext<RegisterFormInput>();
  const error = useFieldError(name);
  const id = fieldId(name);

  return (
    <FormField id={id} error={error}>
      <Checkbox
        id={id}
        label={label}
        invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={describedBy(id, { error })}
        {...register(name)}
      />
    </FormField>
  );
}
